import { topScribeApps } from '../consts';
import { OSTypeForRecorder, OSType, UserAgentBrowser, Plan, EnterprisePlans } from '../enums';
import { TabsData } from '../types';
import { ExperimentalNavigator } from '../types/experimentalBrowserTypes';
import { SuperOrganization } from '../types/organization';

export const safeUrlObject = (url: string | undefined): URL | null => {
  if (!url) {
    return null;
  }
  try {
    return new URL(url);
  } catch (e) {
    return null;
  }
};

export const getTabHostnames = (tabs: chrome.tabs.Tab[]): TabsData => {
  const tabHostnames: TabsData = {};
  tabs.forEach((tab) => {
    const hostName = safeUrlObject(tab?.url)?.hostname;

    if (!hostName || !topScribeApps[hostName]) return;

    tabHostnames[hostName] = {
      ...tab,
      title: topScribeApps[hostName],
    };
  });

  return tabHostnames;
};

/**
 * This function is used to wait for two animation frames before executing some provided code.
 * Useful when you want to take a screenshot of a DOM change that isn't necessarily shown immediately.
 * @param fn The function to execute after two animation frames
 */
export const doAfterTwoAnimatedFrames = (fn: () => void) => {
  requestAnimationFrame(() => {
    requestAnimationFrame(fn);
  });
};

interface UserAgentDataBrands {
  [UserAgentBrowser.Chrome]?: boolean;
  [UserAgentBrowser.Edge]?: boolean;
  [UserAgentBrowser.Brave]?: boolean;
  [UserAgentBrowser.Opera]?: boolean;
}
export const getBrowserName = (): UserAgentBrowser => {
  if (typeof navigator === 'undefined') {
    return UserAgentBrowser.Unknown;
  }

  const userAgentData = (navigator as ExperimentalNavigator)?.userAgentData;

  if (userAgentData?.brands) {
    const brands: UserAgentDataBrands = userAgentData.brands.reduce((acc, data) => {
      if (data.brand.includes('Edge')) {
        acc[UserAgentBrowser.Edge] = true;
      }
      if (data.brand.includes('Brave')) {
        acc[UserAgentBrowser.Brave] = true;
      }
      if (data.brand.includes('Chrome')) {
        acc[UserAgentBrowser.Chrome] = true;
      }
      if (data.brand.includes('Opera')) {
        acc[UserAgentBrowser.Opera] = true;
      }
      return acc;
    }, {} as UserAgentDataBrands);

    if (brands?.['Edge']) {
      return UserAgentBrowser.Edge;
    }
    if (brands?.[UserAgentBrowser.Opera]) {
      return UserAgentBrowser.Opera;
    }
    if (brands?.[UserAgentBrowser.Brave]) {
      return UserAgentBrowser.Brave;
    }
    if (brands?.[UserAgentBrowser.Chrome]) {
      return UserAgentBrowser.Chrome;
    }
  }
  const { userAgent } = navigator;

  if (userAgent.indexOf('Edg') > -1) {
    return UserAgentBrowser.Edge;
  }
  if (userAgent.indexOf('Opera') > -1 || userAgent.indexOf('OPR') > -1) {
    return UserAgentBrowser.Opera;
  }
  if (userAgent.indexOf('Chrome') > -1) {
    return UserAgentBrowser.Chrome;
  }
  if (userAgent.indexOf('Firefox') > -1) {
    return UserAgentBrowser.Firefox;
  }
  if (userAgent.indexOf('Safari') > -1 && /^((?!chrome|android).)*safari/i.test(userAgent)) {
    return UserAgentBrowser.Safari;
  }
  if (userAgent.indexOf('Trident') > -1 || userAgent.indexOf('MSIE') > -1) {
    return UserAgentBrowser.InternetExplorer;
  }

  return UserAgentBrowser.Unknown;
};

export const isEdge = ((): boolean => {
  return getBrowserName() === UserAgentBrowser.Edge;
})();
export const isChrome = ((): boolean => {
  return getBrowserName() === UserAgentBrowser.Chrome;
})();
export const isSafari = ((): boolean => {
  return getBrowserName() === UserAgentBrowser.Safari;
})();
/*
Must wait for page to load, cannot be run in background of extension
Cannot be an IIFE because it needs to be run in the context of the loaded page
*/
export const isArc = (): boolean => {
  return !!window
    .getComputedStyle(document.documentElement)
    .getPropertyValue('--arc-palette-title');
};

export const getBrowserVersion = (): number | null => {
  if (typeof navigator === 'undefined') {
    return null;
  }
  /* Because userAgentData is still technically experimental, it's not on the types we're using
  so must be cast as ExperimentalNavigator. Installing the experimental types via the user-agent-data-types package
  conflicts with the Chrome API types. As you'll see here, we are using properties available
  on userAgentData according to MDN: https://developer.mozilla.org/en-US/docs/Web/API/NavigatorUAData/brands */
  const brands = (navigator as ExperimentalNavigator)?.userAgentData?.brands;

  if (brands) {
    const userChromeVersion = brands?.find(
      (brandInfo: { brand: string; version: string }) => brandInfo.brand === 'Chromium',
    )?.version;

    if (userChromeVersion) {
      return +userChromeVersion;
    }
    return null;
  }
  const { userAgent } = navigator;
  let version: string | null = null;

  if (userAgent.includes('Chrome')) {
    const chromeVersionMatch = userAgent.match(/Chrome\/(\d+)/);

    version = chromeVersionMatch ? chromeVersionMatch[1] : null;
  } else if (userAgent.includes('Edg')) {
    const edgeVersionMatch = userAgent.match(/Edg\/(\d+)/);

    version = edgeVersionMatch ? edgeVersionMatch[1] : null;
  }

  return version ? +version : null;
};

export function userOnXVersionOfChromeOrAbove(requiredVersionNumber: number): boolean {
  const detectedChromeVersion = getBrowserVersion();

  if (detectedChromeVersion && detectedChromeVersion >= requiredVersionNumber) {
    return true;
  }
  return false;
}
/**
 *
 * @returns {'Mac OS' | 'iOS' | 'Windows' | 'Android' | 'Linux' | null}
 */
export const getOS = (): OSType | null => {
  let os: OSType | null = null;

  if (self.navigator) {
    const userAgentData = (navigator as ExperimentalNavigator)?.userAgentData;
    /* By default, experimental features are not included in our types so we've added
  the interface here according to the properties listed on MDN: https://developer.mozilla.org/en-US/docs/Web/API/NavigatorUAData */

    if (userAgentData) {
      const { platform } = userAgentData;

      if (platform) {
        switch (platform) {
          case 'macOS':
          case 'Macintosh':
          case 'MacIntel':
          case 'MacPPC':
          case 'Mac68K':
            return OSType.MacOS;
          case 'iPhone':
          case 'iPad':
          case 'iPod':
            return OSType.iOS;
          case 'Win32':
          case 'Win64':
          case 'Windows':
          case 'WinCE':
            return OSType.Windows;
          case 'Android':
            return OSType.Android;
          case 'Linux':
            return OSType.Linux;
          default:
            return null;
        }
      }
    }
    const { userAgent } = self.navigator;
    /* Platform is deprecated, thus it is the fallback and not the primary here */
    const { platform } = self.navigator;
    const macosPlatforms = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'];
    const windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'];
    const iosPlatforms = ['iPhone', 'iPad', 'iPod'];

    if (macosPlatforms.indexOf(platform) !== -1) {
      os = OSType.MacOS;
    } else if (iosPlatforms.indexOf(platform) !== -1) {
      os = OSType.iOS;
    } else if (windowsPlatforms.indexOf(platform) !== -1) {
      os = OSType.Windows;
    } else if (/Android/.test(userAgent)) {
      os = OSType.Android;
    } else if (/Linux/.test(platform)) {
      os = OSType.Linux;
    }
  }

  return os;
};

export const getOSForRecorder = (): OSTypeForRecorder | null => {
  const os = getOS();
  let osForRecorder: OSTypeForRecorder | null = null;

  if (os) {
    osForRecorder = os.replace(/\s+/, '').toLowerCase() as OSTypeForRecorder;
  }
  return osForRecorder;
};

export const checkForBlockingExtensions = async (): Promise<{
  onepasswordNightly: boolean;
  onepassword: boolean;
}> => {
  /* Hack to detect blocking extensions without requesting for new chrome permissions
  fetching a resource listed under their manifest web_accessible_resources,
  attempt to open side panel if none of below are detected */
  /*
  1Password + 1Password Nightly: block the ability to open the side panel from content script
  */
  const sidePanelBlockList = [
    {
      extensionId: 'gejiddohjgogedgjnonbofjigllpkmbf',
      path: 'inline/injected/styles/inline-tooltip.css',
    },
    {
      extensionId: 'aeblfdkhhhdcdjpifhhbdiojplfjncoa',
      path: 'inline/injected/styles/inline-tooltip.css',
    },
  ];
  const extensionPromises = sidePanelBlockList.map(({ extensionId, path }) =>
    fetch(`chrome-extension://${extensionId}/${path}`)
      .then(() => true)
      .catch(() => false),
  );
  return Promise.all(extensionPromises).then(([isNightlyDetected, isStandardDetected]) => {
    return {
      onepasswordNightly: isNightlyDetected,
      onepassword: isStandardDetected,
    };
  });
};

// If url = https://www.figma.com/design/c4fW1ti4VwDqkXE7CRFgGN/Ask-Scribe?node-id=577-7185&node-type=canvas&m=dev
// returns "Figma"
export const extractCapitalizedDomainName = (input: string | undefined): string => {
  if (!input) return '';

  try {
    // Remove protocol if present
    let url = input.trim().toLowerCase();
    url = url.replace(/^(https?:\/\/)/, '');

    // Remove everything after first slash (path, query, etc)
    url = url.split('/')[0];

    // Remove leading "www.", "ww.", or similar
    url = url.replace(/^w{2,3}\./, '');

    // Remove port if present
    url = url.split(':')[0];

    // If only a word is left, return it
    if (!url.includes('.')) {
      return url.charAt(0).toUpperCase() + url.slice(1);
    }

    // Split by dot and get the second-to-last part (the domain)
    const parts = url.split('.');
    // Handle cases like "google.com" or "subdomain.google.com"
    const domain = parts.length > 1 ? parts[parts.length - 2] : parts[0];

    return domain.charAt(0).toUpperCase() + domain.slice(1);
  } catch {
    return '';
  }
};

interface UserData {
  active_organization?: {
    super_organization?: SuperOrganization;
  };
}

export const isSuperOrgPlanEnterprise = (superOrg?: SuperOrganization): boolean => {
  if (!superOrg?.plan?.name) return false;
  return (EnterprisePlans as readonly string[]).includes(superOrg.plan.name);
};

export const isUserInEnterpriseSuperOrg = (userData?: UserData): boolean => {
  return isSuperOrgPlanEnterprise(userData?.active_organization?.super_organization);
};

export const isSuperOrgPlanProTeam = (superOrg: SuperOrganization) =>
  superOrg?.plan?.name === Plan.PRO &&
  superOrg?.stripe_subscription_status === 'active' &&
  superOrg?.pro_plan_type === 'five_user_team';
